// 📄 pages/MiEspacio/MiEspacioTabs.tsx
// Tabs + las 4 secciones de Mi Espacio. Compartido entre la vista Global
// del Home (/mi-espacio, con filtro de proyecto/contexto — ver
// MiEspacioHome.tsx) y "Mi Espacio en el Proyecto" (dentro de un proyecto,
// con un único contexto fijo — ver pages/Proyectos/MiEspacioProyecto.tsx).
import { useState } from 'react';
import { Code2, FileWarning, ListChecks, Contact2, FileText, Sparkles } from 'lucide-react';
import SnippetsSection from './sections/SnippetsSection.tsx';
import NotasSapSection from './sections/NotasSapSection.tsx';
import TareasSection from './sections/TareasSection.tsx';
import ContactosSection from './sections/ContactosSection.tsx';
import ArchivosSection from './sections/ArchivosSection.tsx';
import BusquedaIASection from './sections/BusquedaIASection.tsx';
import { MiEspacioSeccionProps } from './miEspacioContexto.ts';

type MiEspacioTab = 'snippets' | 'notas' | 'tareas' | 'contactos' | 'archivos' | 'busqueda-ia';

const TABS: { id: MiEspacioTab; label: string; icon: typeof Code2 }[] = [
  { id: 'snippets', label: 'Snippets & Código Z', icon: Code2 },
  { id: 'notas', label: 'Notas de SAP y Soluciones', icon: FileWarning },
  { id: 'tareas', label: 'Notas & Pendientes', icon: ListChecks },
  { id: 'contactos', label: 'Directorio de Contactos', icon: Contact2 },
  { id: 'archivos', label: 'Archivos', icon: FileText },
  { id: 'busqueda-ia', label: 'Buscar con IA', icon: Sparkles }
];

const TAB_IDS = TABS.map((t) => t.id);

interface MiEspacioTabsProps extends MiEspacioSeccionProps {
  // Acceso directo desde el Home (chip "Buscar con IA", ver
  // HomeLanding.tsx → MiEspacioHome.tsx) — arranca en esta pestaña en vez
  // de Snippets. Un valor inválido/ausente cae al default de siempre.
  tabInicial?: string;
}

export default function MiEspacioTabs({ tabInicial, ...props }: MiEspacioTabsProps) {
  const [tab, setTab] = useState<MiEspacioTab>(
    TAB_IDS.includes(tabInicial as MiEspacioTab) ? (tabInicial as MiEspacioTab) : 'snippets'
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm transition-all ${
              tab === id
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'snippets' && <SnippetsSection {...props} />}
      {tab === 'notas' && <NotasSapSection {...props} />}
      {tab === 'tareas' && <TareasSection {...props} />}
      {tab === 'contactos' && <ContactosSection {...props} />}
      {tab === 'archivos' && <ArchivosSection {...props} />}
      {tab === 'busqueda-ia' && <BusquedaIASection {...props} />}
    </div>
  );
}
